/**
 * The Step 1 panel's contract, enforced as structured validation
 * (DESIGN_RECORD item 5; brief Sections 6.1-6.2 and 12.3).
 *
 * Microdata (one row per respondent) must carry: an area code, a
 * survey weight, the outcome (indicator) columns the analysis will
 * estimate, and the design variables (strata/PSU) when the design
 * uses them. Area-level tables (one row per area) must carry: an
 * area code, a direct estimate, a sampling variance OR a standard
 * error, a sample size, and any covariates named for the model.
 *
 * Every problem is an IoIssue naming the column and the problem in
 * plain language. Severity discipline: an issue is an error when the
 * engine cannot run correctly without the data; a warning when the
 * run proceeds but something was excluded or deserves a look.
 */
import { columnIndex } from './types.js';
function missingColumn(column, role) {
    return {
        code: 'IO_MISSING_COLUMN',
        severity: 'error',
        message: `The file has no column named "${column}", which is needed as the ${role}. Check the spelling, or map a different column to this role.`,
        column,
    };
}
function present(table, name) {
    return columnIndex(table, name) >= 0;
}
function cells(table, name) {
    const i = columnIndex(table, name);
    return table.rows.map((r) => r[i] ?? null);
}
function isMissing(v) {
    return v === null || (typeof v === 'string' && v.trim() === '');
}
function describeRows(rows) {
    const shown = rows.slice(0, 5).join(', ');
    return rows.length > 5 ? `${shown}, ...` : shown;
}
// ------------------------------------------------- frame PSU counts
/**
 * The engine's stratum key for records that carry no stratum
 * value (mirrors SINGLE_STRATUM in the engine's direct.ts, which
 * the engine package does not export; feasibility.ts mirrors
 * engine constants the same way).
 */
export const SINGLE_STRATUM_KEY = '__single_stratum__';
/**
 * Scan a mapped frame PSU-count column (MicrodataRoles.psuPopulation)
 * stratum by stratum, over exactly the rows the converter keeps
 * (rows with an area code), keyed exactly as the engine keys
 * strata (String(stratum value); the single-stratum sentinel when
 * a row carries no stratum). A row with no PSU value is its own
 * PSU, as in the engine (brief ING-01), so the sampled count is
 * the distinct PSU values plus one per PSU-less row. Both the
 * validator and the converter read this one scan, so the counts a
 * run consumes are the counts validation approved.
 */
export function scanFrameCounts(table, roles) {
    const iArea = columnIndex(table, roles.area);
    const iStratum = roles.stratum ? columnIndex(table, roles.stratum) : -1;
    const iPsu = roles.psu ? columnIndex(table, roles.psu) : -1;
    const iFrame = roles.psuPopulation
        ? columnIndex(table, roles.psuPopulation)
        : -1;
    const groups = new Map();
    const invalidRows = [];
    table.rows.forEach((row, r) => {
        const areaCell = row[iArea];
        if (areaCell === null || areaCell === '')
            return; // converter drops it
        const stratumCell = iStratum >= 0 ? row[iStratum] : null;
        const key = stratumCell === null || stratumCell === undefined
            ? SINGLE_STRATUM_KEY
            : String(stratumCell);
        let g = groups.get(key);
        if (!g) {
            g = { values: [], seen: new Set(), psus: new Set(), psuLessRows: 0 };
            groups.set(key, g);
        }
        const psuCell = iPsu >= 0 ? row[iPsu] : null;
        if (psuCell === null || psuCell === undefined)
            g.psuLessRows += 1;
        else
            g.psus.add(String(psuCell));
        if (iFrame < 0)
            return;
        const v = row[iFrame];
        if (v === null || v === undefined || v === '')
            return;
        if (!g.seen.has(v)) {
            g.seen.add(v);
            g.values.push(v);
        }
        if (typeof v !== 'number' || !Number.isInteger(v) || v <= 0) {
            invalidRows.push(r + 1);
        }
    });
    return {
        strata: [...groups.entries()].map(([key, g]) => ({
            key,
            values: g.values,
            sampledPsus: g.psus.size + g.psuLessRows,
        })),
        invalidRows,
    };
}
/**
 * The per-stratum frame PSU counts a run consumes, keyed as the
 * engine keys strata. Present only when at least one stratum
 * carries a count; strata without one are absent from the map
 * (the engine treats them as sampled with replacement, the
 * documented fallback of Addendum 30). Call only when validation
 * reported no frame-count errors: the scan's agreed values are
 * then single positive integers per stratum.
 */
export function framePsuCountMap(table, roles) {
    if (!roles.psuPopulation)
        return undefined;
    const scan = scanFrameCounts(table, roles);
    const map = new Map();
    for (const s of scan.strata) {
        const v = s.values.length === 1 ? s.values[0] : undefined;
        if (typeof v === 'number' &&
            Number.isInteger(v) &&
            v > 0 &&
            v >= s.sampledPsus) {
            map.set(s.key, v);
        }
    }
    return map.size > 0 ? map : undefined;
}
/**
 * Validation of a mapped frame PSU-count column (Decision Log
 * Addendum 37, criterion 3): a stratum's count must be one agreed
 * positive whole number, and no smaller than the number of PSUs
 * actually sampled in that stratum (a count equal to the sampled
 * number is the certainty case, f = 1). Violations are errors —
 * the run cannot proceed on a frame declaration that contradicts
 * itself or the sample. Strata carrying no count at all are legal
 * (the engine's with-replacement fallback) but are named in a
 * warning when other strata do carry counts, so a partial frame
 * is never silent.
 */
export function validateFrameCounts(table, roles) {
    const issues = [];
    const column = roles.psuPopulation;
    const scan = scanFrameCounts(table, roles);
    const named = (key) => key === SINGLE_STRATUM_KEY ? 'the design (no stratum variable)' : `stratum "${key}"`;
    if (scan.invalidRows.length > 0) {
        issues.push({
            code: 'IO_FRAME_COUNT_INVALID',
            severity: 'error',
            message: `The frame PSU-count column "${column}" has ${scan.invalidRows.length} value${scan.invalidRows.length === 1 ? '' : 's'} that are not positive whole numbers (rows ${describeRows([...scan.invalidRows])}). A frame count is the number of PSUs in the population for a stratum: a positive whole number, the same on every row of the stratum.`,
            column,
        });
        return issues;
    }
    let covered = 0;
    const uncovered = [];
    for (const s of scan.strata) {
        if (s.values.length === 0) {
            uncovered.push(s.key);
            continue;
        }
        covered += 1;
        if (s.values.length > 1) {
            issues.push({
                code: 'IO_FRAME_COUNT_INCONSISTENT',
                severity: 'error',
                message: `The frame PSU-count column "${column}" gives different counts for ${named(s.key)} (${s.values.join(', ')}). A stratum has one frame count; correct the column, or remove its role assignment if it is not a frame count.`,
                column,
            });
            continue;
        }
        const count = s.values[0];
        if (count < s.sampledPsus) {
            issues.push({
                code: 'IO_FRAME_COUNT_TOO_SMALL',
                severity: 'error',
                message: `The frame PSU count for ${named(s.key)} is ${count}, but ${s.sampledPsus} PSUs were sampled in that stratum. The frame cannot hold fewer PSUs than were sampled from it; check the count, or the stratum and PSU columns.`,
                column,
            });
        }
    }
    if (covered > 0 && uncovered.length > 0) {
        const shown = uncovered.slice(0, 5).map(named).join(', ');
        issues.push({
            code: 'IO_FRAME_COUNT_PARTIAL',
            severity: 'warning',
            message: `No frame PSU count is given for ${shown}${uncovered.length > 5 ? ` and ${uncovered.length - 5} more` : ''}. Those strata are treated as sampled with replacement (no finite population correction); the strata with counts use the corrected basis.`,
            column,
        });
    }
    else if (covered === 0) {
        issues.push({
            code: 'IO_FRAME_COUNT_EMPTY',
            severity: 'warning',
            message: `The frame PSU-count column "${column}" is empty on every row, so no frame counts are used and the design is treated as sampled with replacement.`,
            column,
        });
    }
    return issues;
}
// ------------------------------------------------------------- microdata
export function validateMicrodataTable(table, roles) {
    const issues = [];
    if (table.rows.length === 0) {
        issues.push({
            code: 'IO_EMPTY_TABLE',
            severity: 'error',
            message: 'The file has columns but no respondent rows.',
        });
        return issues;
    }
    if (!present(table, roles.area))
        issues.push(missingColumn(roles.area, 'area code'));
    if (!present(table, roles.weight))
        issues.push(missingColumn(roles.weight, 'survey weight'));
    if (roles.stratum && !present(table, roles.stratum)) {
        issues.push(missingColumn(roles.stratum, 'strata variable'));
    }
    if (roles.psu && !present(table, roles.psu)) {
        issues.push(missingColumn(roles.psu, 'PSU (cluster) variable'));
    }
    if (roles.psuPopulation && !present(table, roles.psuPopulation)) {
        issues.push(missingColumn(roles.psuPopulation, 'frame PSU-count variable'));
    }
    for (const ind of roles.indicators) {
        if (!present(table, ind))
            issues.push(missingColumn(ind, 'outcome column'));
    }
    for (const sub of roles.subgroups ?? []) {
        if (!present(table, sub))
            issues.push(missingColumn(sub, 'subgroup column'));
    }
    if (issues.some((i) => i.severity === 'error'))
        return issues;
    // Area ids: rows without one cannot be placed; they are excluded
    // by the converter and named here as a count with example rows.
    const areaCells = cells(table, roles.area);
    const noArea = areaCells
        .map((v, r) => (isMissing(v) ? r + 1 : -1))
        .filter((r) => r > 0);
    if (noArea.length > 0) {
        issues.push({
            code: 'IO_AREA_MISSING',
            severity: 'warning',
            message: `${noArea.length} respondent row${noArea.length === 1 ? ' has' : 's have'} no area code in "${roles.area}" and will be excluded (rows ${describeRows(noArea)}).`,
            column: roles.area,
        });
    }
    // Weights: every included respondent needs a positive number.
    const wCells = cells(table, roles.weight);
    let wBad = 0;
    let wNonPositive = 0;
    wCells.forEach((v, r) => {
        if (noArea.includes(r + 1))
            return;
        if (typeof v !== 'number')
            wBad++;
        else if (!(v > 0))
            wNonPositive++;
    });
    if (wBad > 0) {
        issues.push({
            code: 'IO_WEIGHT_INVALID',
            severity: 'error',
            message: `The weight column "${roles.weight}" has ${wBad} row${wBad === 1 ? '' : 's'} with a missing or non-numeric weight. Every respondent needs a numeric weight.`,
            column: roles.weight,
        });
    }
    if (wNonPositive > 0) {
        issues.push({
            code: 'IO_WEIGHT_NONPOSITIVE',
            severity: 'error',
            message: `The weight column "${roles.weight}" has ${wNonPositive} row${wNonPositive === 1 ? '' : 's'} with a weight of zero or less. Analysis weights must be positive.`,
            column: roles.weight,
        });
    }
    // Indicators: the engine consumes 0/1 outcomes; anything else must
    // be recoded by an explicit rule (Block 3), never guessed here.
    for (const ind of roles.indicators) {
        const bad = new Set();
        for (const v of cells(table, ind)) {
            if (v === null)
                continue;
            if (v !== 0 && v !== 1)
                bad.add(v);
        }
        if (bad.size > 0) {
            const shown = [...bad].slice(0, 5).join(', ');
            issues.push({
                code: 'IO_INDICATOR_VALUES',
                severity: 'error',
                message: `The outcome column "${ind}" contains values other than 0 and 1 (${shown}${bad.size > 5 ? ', ...' : ''}). Recode it to 0/1 with an explicit recode rule before running.`,
                column: ind,
            });
        }
    }
    for (const [name, code, label] of [
        [roles.stratum, 'IO_STRATUM_MISSING', 'strata'],
        [roles.psu, 'IO_PSU_MISSING', 'PSU'],
    ]) {
        if (!name)
            continue;
        const n = cells(table, name).filter((v) => isMissing(v)).length;
        if (n > 0) {
            issues.push({
                code,
                severity: 'warning',
                message: `${n} row${n === 1 ? ' has' : 's have'} no ${label} value in "${name}". Those respondents form singleton strata in variance estimation.`,
                column: name,
            });
        }
    }
    // Frame PSU counts (Addendum 37): validated against the sample
    // itself — one agreed positive whole number per stratum, at
    // least the sampled PSU count of that stratum.
    if (roles.psuPopulation && present(table, roles.psuPopulation)) {
        issues.push(...validateFrameCounts(table, roles));
    }
    return issues;
}
// ------------------------------------------------------------ area-level
export function validateAreaLevelTable(table, roles, options = {}) {
    const issues = [];
    const scale = options.estimateScale ?? 'proportion';
    if (table.rows.length === 0) {
        issues.push({
            code: 'IO_EMPTY_TABLE',
            severity: 'error',
            message: 'The file has columns but no area rows.',
        });
        return issues;
    }
    if (!present(table, roles.area))
        issues.push(missingColumn(roles.area, 'area code'));
    if (!present(table, roles.estimate))
        issues.push(missingColumn(roles.estimate, 'direct estimate'));
    if (!present(table, roles.sampleSize))
        issues.push(missingColumn(roles.sampleSize, 'sample size'));
    if (roles.variance && !present(table, roles.variance)) {
        issues.push(missingColumn(roles.variance, 'sampling variance'));
    }
    if (roles.standardError && !present(table, roles.standardError)) {
        issues.push(missingColumn(roles.standardError, 'standard error'));
    }
    if (roles.populationCount && !present(table, roles.populationCount)) {
        issues.push(missingColumn(roles.populationCount, 'population count'));
    }
    for (const cov of roles.covariates ?? []) {
        if (!present(table, cov))
            issues.push(missingColumn(cov, 'covariate'));
    }
    if (!roles.variance && !roles.standardError) {
        issues.push({
            code: 'IO_VARIANCE_SOURCE_MISSING',
            severity: 'error',
            message: 'No sampling-variance or standard-error column was mapped. An area-level file needs one of the two for every area.',
        });
    }
    if (roles.variance && roles.standardError) {
        issues.push({
            code: 'IO_VARIANCE_SOURCE_AMBIGUOUS',
            severity: 'warning',
            message: `Both a variance column ("${roles.variance}") and a standard-error column ("${roles.standardError}") were mapped; the variance column is used.`,
        });
    }
    if (issues.some((i) => i.severity === 'error'))
        return issues;
    // Area ids: one row per area, all identified.
    const areaCells = cells(table, roles.area);
    const missingAreas = [];
    const seenAreas = new Map();
    const dupAreas = new Set();
    areaCells.forEach((v, r) => {
        if (isMissing(v)) {
            missingAreas.push(`row ${r + 1}`);
            return;
        }
        const key = String(v);
        if (seenAreas.has(key))
            dupAreas.add(key);
        seenAreas.set(key, (seenAreas.get(key) ?? 0) + 1);
    });
    if (missingAreas.length > 0) {
        issues.push({
            code: 'IO_AREA_MISSING',
            severity: 'error',
            message: `${missingAreas.length} row${missingAreas.length === 1 ? ' has' : 's have'} no area code in "${roles.area}" (${missingAreas.slice(0, 5).join(', ')}). Every area-level row needs its area code.`,
            column: roles.area,
        });
    }
    if (dupAreas.size > 0) {
        const shown = [...dupAreas].slice(0, 5).join(', ');
        issues.push({
            code: 'IO_DUPLICATE_AREA',
            severity: 'error',
            message: `The area code${dupAreas.size === 1 ? '' : 's'} ${shown}${dupAreas.size > 5 ? ', ...' : ''} appear${dupAreas.size === 1 ? 's' : ''} on more than one row. An area-level file must have exactly one row per area; combine or remove the duplicates.`,
            column: roles.area,
        });
    }
    // Estimates.
    const estCells = cells(table, roles.estimate);
    const estMissing = [];
    let estBad = 0;
    const estRange = [];
    const estMax = scale === 'percent' ? 100 : 1;
    estCells.forEach((v, r) => {
        const area = String(areaCells[r] ?? `row ${r + 1}`);
        if (isMissing(v)) {
            estMissing.push(area);
        }
        else if (typeof v !== 'number') {
            estBad++;
        }
        else if (v < 0 || v > estMax) {
            estRange.push(area);
        }
    });
    if (estBad > 0) {
        issues.push({
            code: 'IO_ESTIMATE_INVALID',
            severity: 'error',
            message: `The estimate column "${roles.estimate}" has ${estBad} non-numeric value${estBad === 1 ? '' : 's'}. Estimates must be numbers.`,
            column: roles.estimate,
        });
    }
    if (estMissing.length > 0) {
        issues.push({
            code: 'IO_ESTIMATE_MISSING',
            severity: 'warning',
            message: `No direct estimate for ${estMissing.slice(0, 5).join(', ')}${estMissing.length > 5 ? ` and ${estMissing.length - 5} more` : ''}. Those areas are excluded from the model fit and named in the QC report; their covariates still inform predictions if provided.`,
            column: roles.estimate,
        });
    }
    if (estRange.length > 0) {
        // A blocking refusal, not a warning (Tier 3 verdict, 2026-11-01,
        // Section 3 and required fix 3): SAE Studio estimates proportions
        // only, and before this change the run proceeded past the warning
        // and the estimates were clamped into range and published. The
        // engine refuses the same inputs again at estimation time
        // (NonProportionEstimateError), so no caller can publish them.
        const areas = `${estRange.slice(0, 5).join(', ')}` +
            `${estRange.length > 5 ? ` and ${estRange.length - 5} more` : ''}`;
        issues.push({
            code: 'IO_ESTIMATE_RANGE',
            severity: 'error',
            message: scale === 'percent'
                ? `SAE Studio estimates proportions, entered here on the 0 to 100 percent scale; the estimate column "${roles.estimate}" has values outside that range for ${areas}. Nothing was estimated.`
                : `SAE Studio estimates proportions between 0 and 1; the estimate column "${roles.estimate}" has values outside that range for ${areas}. Nothing was estimated. If the file stores percentages (0-100), re-run with the percent scale; nothing was converted automatically.`,
            column: roles.estimate,
        });
    }
    // Variance / standard error.
    const varName = roles.variance ?? roles.standardError;
    if (varName) {
        const varCells = cells(table, varName);
        const missing = [];
        let negative = 0;
        let zero = 0;
        let bad = 0;
        varCells.forEach((v, r) => {
            if (isMissing(v))
                missing.push(String(areaCells[r] ?? `row ${r + 1}`));
            else if (typeof v !== 'number')
                bad++;
            else if (v < 0)
                negative++;
            else if (v === 0)
                zero++;
        });
        const noun = roles.variance ? 'sampling variance' : 'standard error';
        if (bad > 0) {
            issues.push({
                code: 'IO_VARIANCE_INVALID',
                severity: 'error',
                message: `The ${noun} column "${varName}" has ${bad} non-numeric value${bad === 1 ? '' : 's'}.`,
                column: varName,
            });
        }
        if (negative > 0) {
            issues.push({
                code: roles.variance ? 'IO_VARIANCE_NEGATIVE' : 'IO_SE_NEGATIVE',
                severity: 'error',
                message: `The ${noun} column "${varName}" has ${negative} negative value${negative === 1 ? '' : 's'}. A ${noun} cannot be negative; check the file.`,
                column: varName,
            });
        }
        if (zero > 0) {
            issues.push({
                code: roles.variance ? 'IO_VARIANCE_ZERO' : 'IO_SE_ZERO',
                severity: 'warning',
                message: `${zero} area${zero === 1 ? ' has' : 's have'} a ${noun} of zero in "${varName}". A zero ${noun} claims perfect precision and will distort the model; verify before running.`,
                column: varName,
            });
        }
        if (missing.length > 0) {
            issues.push({
                code: 'IO_VARIANCE_MISSING',
                severity: 'warning',
                message: `No ${noun} for ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? ` and ${missing.length - 5} more` : ''}. Those areas cannot enter the model fit.`,
                column: varName,
            });
        }
    }
    // Sample size.
    const nCells = cells(table, roles.sampleSize);
    let nMissing = 0;
    let nNegative = 0;
    let nZero = 0;
    let nFractional = 0;
    for (const v of nCells) {
        if (isMissing(v) || typeof v !== 'number')
            nMissing++;
        else if (v < 0)
            nNegative++;
        else if (v === 0)
            nZero++;
        else if (!Number.isInteger(v))
            nFractional++;
    }
    if (nMissing > 0) {
        issues.push({
            code: 'IO_N_MISSING',
            severity: 'error',
            message: `The sample-size column "${roles.sampleSize}" is missing or non-numeric for ${nMissing} area${nMissing === 1 ? '' : 's'}. Every area needs its respondent count.`,
            column: roles.sampleSize,
        });
    }
    if (nNegative > 0) {
        issues.push({
            code: 'IO_N_NEGATIVE',
            severity: 'error',
            message: `The sample-size column "${roles.sampleSize}" has ${nNegative} negative value${nNegative === 1 ? '' : 's'}.`,
            column: roles.sampleSize,
        });
    }
    if (nZero > 0) {
        issues.push({
            code: 'IO_N_ZERO',
            severity: 'warning',
            message: `${nZero} area${nZero === 1 ? ' has' : 's have'} a sample size of zero in "${roles.sampleSize}". Those areas have no respondents; they can still receive model predictions from their covariates.`,
            column: roles.sampleSize,
        });
    }
    if (nFractional > 0) {
        issues.push({
            code: 'IO_N_FRACTIONAL',
            severity: 'warning',
            message: `The sample-size column "${roles.sampleSize}" has ${nFractional} non-whole value${nFractional === 1 ? '' : 's'}. Sample sizes are respondent counts and are normally whole numbers.`,
            column: roles.sampleSize,
        });
    }
    // Covariates: the model needs a numeric value for every area
    // (brief ING-03).
    for (const cov of roles.covariates ?? []) {
        const covCells = cells(table, cov);
        let missingCount = 0;
        let badCount = 0;
        for (const v of covCells) {
            if (isMissing(v))
                missingCount++;
            else if (typeof v !== 'number')
                badCount++;
        }
        if (badCount > 0) {
            issues.push({
                code: 'IO_COVARIATE_INVALID',
                severity: 'error',
                message: `The covariate column "${cov}" has ${badCount} non-numeric value${badCount === 1 ? '' : 's'}. Covariates must be numbers.`,
                column: cov,
            });
        }
        if (missingCount > 0) {
            issues.push({
                code: 'IO_COVARIATE_MISSING',
                severity: 'warning',
                message: `The covariate "${cov}" is missing for ${missingCount} area${missingCount === 1 ? '' : 's'}. Areas without a complete covariate row cannot receive model estimates.`,
                column: cov,
            });
        }
    }
    // Population counts.
    if (roles.populationCount) {
        const popCells = cells(table, roles.populationCount);
        let bad = 0;
        for (const v of popCells) {
            if (v !== null && (typeof v !== 'number' || v <= 0))
                bad++;
        }
        if (bad > 0) {
            issues.push({
                code: 'IO_POPULATION_INVALID',
                severity: 'warning',
                message: `The population column "${roles.populationCount}" has ${bad} missing, non-numeric, or non-positive value${bad === 1 ? '' : 's'}. Those counts are ignored.`,
                column: roles.populationCount,
            });
        }
    }
    return issues;
}
//# sourceMappingURL=validate.js.map